<template>
  <div ref="editor_root">
    <div
      :id="$attrs.id"
      ref="editorContainer"
      :class="[
        'relative flex flex-col rounded-2xl border p-2 pt-0',
        { 'transition-[height] ease-out': !is_resizing },
        is_error ? error_border_class : 'border-gray-200',
        { disabled: disabled },
      ]"
      :style="[editorBackgroundColor, { height: current_height + 'px' }]"
    >
      <div class="flex shrink-0 items-center justify-between">
        <slot name="header" />
      </div>
      <EditorContent
        class="h-full grow"
        data-test="editor-content"
        v-bind="editorContentProps"
        @click="editor?.commands.focus()"
      />
      <div v-if="attachments_pending.length > 0">
        <div class="tags shrink-0">
          <div
            v-for="attachment in attachments_pending"
            :key="attachment.id"
            class="flex items-center justify-between rounded-full bg-gray-700 px-3 py-1 text-white"
            data-test="ticket-attachment-pending"
          >
            <p class="text-sm">{{ attachment.attributes.name }}</p>
            <button
              class="delete ml-3"
              @click.stop="$emit('removeAttachment', attachment.id)"
            />
          </div>
        </div>
      </div>
      <div
        v-if="!$_.isNil(editor)"
        :editor="editor"
        class="flex shrink-0"
      >
        <div
          v-if="isMobile"
          class="flex items-center gap-1"
        >
          <button
            :class="showMobileToolbar ? 'bg-gray-200' : 'bg-white!'"
            :disabled="disabled"
            aria-label="Toggle formatting toolbar"
            class="flex size-8 shrink-0 items-center justify-center rounded-full border hover:bg-gray-200 hover:text-gray-800"
            @click="toggleMobileToolbar"
          >
            <span v-if="!showMobileToolbar">Aa</span>
            <span
              v-else
              class="text-sm font-bold"
            >
              <FontAwesomeIcon :icon="['far', 'xmark']" />
            </span>
          </button>
          <div
            :class="{ 'mobile-toolbar-visible': showMobileToolbar }"
            class="mobile-toolbar-container"
          >
            <div class="flex w-full flex-col gap-2">
              <div class="flex flex-wrap gap-1">
                <button
                  v-for="toolbarItem in toolbarItems"
                  :key="toolbarItem.name"
                  v-tooltip.bottom="$t(`ticket.editor.tooltips.${toolbarItem.name}`)"
                  :aria-label="$t(`editor_ziggu.actions.${toolbarItem.name}`)"
                  :aria-pressed="toolbarItem.isActive"
                  :class="{ 'bg-gray-200!': toolbarItem.isActive }"
                  class="size-8 rounded-full border border-gray-400! bg-transparent! text-gray-700! hover:bg-green-100! hover:text-green-900!"
                  @click="toolbarItem.action($event)"
                >
                  <FontAwesomeIcon :icon="toolbarItem.icon" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          v-else
          class="flex flex-wrap items-end gap-2"
        >
          <div class="flex grow gap-1">
            <button
              v-for="toolbarItem in toolbarItems"
              :key="toolbarItem.name"
              v-tooltip.bottom="$t(`ticket.editor.tooltips.${toolbarItem.name}`)"
              :aria-pressed="toolbarItem.isActive"
              :class="{ 'bg-gray-300!': toolbarItem.isActive }"
              class="size-8 rounded-full border border-gray-400! bg-transparent! text-gray-700! hover:bg-green-100! hover:text-green-900!"
              @click="toolbarItem.action($event)"
            >
              <FontAwesomeIcon :icon="toolbarItem.icon" />
            </button>
          </div>
        </div>
        <div class="grow" />
        <slot />
      </div>
      <LinkModal
        :initial-url="linkUrl"
        :selected-text="selectedText"
        :visible="linkMenuIsActive"
        @close="cancelLink"
        @submit="confirmLink"
      />
      <ColorPicker
        ref="highlight_picker_ref"
        type="highlight"
        :visible="show_highlight_menu"
        :editor="editor"
        @select="setHighlightColor"
        @remove="removeHighlight"
        @close="closeHighlightMenu"
      />
      <ColorPicker
        ref="text_color_picker_ref"
        type="textColor"
        :visible="show_text_color_menu"
        :editor="editor"
        @select="setTextColor"
        @remove="removeTextColor"
        @close="closeTextColorMenu"
      />
      <div
        :class="[
          'absolute z-50 flex cursor-row-resize items-center justify-center rounded p-1 text-gray-400 hover:text-gray-600',
          resize_bottom ? 'right-1 bottom-1' : 'top-1 right-1',
        ]"
        @pointerdown="startResize"
      >
        <FontAwesomeIcon :icon="['far', 'grip-lines']" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { EditorContent } from '@tiptap/vue-3';
import 'typeface-inter';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import LinkModal from '@/app/shared_components/modals/LinkModal.vue';
import ColorPicker from '@/app/shared_components/ColorPicker.vue';
import { STATUS, STATUS_COLORS } from '@/app/data/status_constants';
import useEditorResize from '@/composables/useEditorResize';
import useEditorLinks from '@/composables/useEditorLinks';
import useEditorColors from '@/composables/useEditorColors';
import useEditorToolbar from '@/composables/useEditorToolbar';
import useEditorInit from '@/composables/useEditorInit';

const props = defineProps({
  content_html: { type: String, default: '' },
  focus_when_mounted: { type: Boolean, default: false },
  is_ticket: { type: Boolean, default: false },
  content_key: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  is_error: { type: Boolean },
  placeholder: { type: String, default: '' },
  with_mentions: { type: Boolean },
  project_id: { type: String, default: '' },
  signature_attached: { type: Boolean },
  min_height: { type: Number, default: 52 },
  type: { type: String, default: '' },
  attachments_pending: { type: Array, default: () => [] },
  resize_bottom: { type: Boolean, default: true },
});

const emit = defineEmits(['removeAttachment', 'update:content_html']);

const editor_root = ref(null);
const editorContainer = ref(null);
const editor = ref(null);
const isMobile = ref(false);
const showMobileToolbar = ref(false);
const error_border_class = STATUS_COLORS[STATUS.ERROR].CARD_BORDER;

function checkMobile() {
  isMobile.value = window.innerWidth <= 768;
}
checkMobile();

function toggleMobileToolbar() {
  showMobileToolbar.value = !showMobileToolbar.value;
}

const { is_resizing, current_height, startResize } = useEditorResize({
  is_ticket: computed(() => props.is_ticket),
  resize_bottom: computed(() => props.resize_bottom),
  editor_container: editorContainer,
  root_el: editor_root,
  on_window_resize: checkMobile,
});

const {
  link_url: linkUrl,
  link_menu_is_active: linkMenuIsActive,
  selected_text: selectedText,
  setLink,
  confirmLink,
  cancelLink,
  closeLinkMenu,
} = useEditorLinks({
  editor,
  disabled: computed(() => props.disabled),
});

const { clearContent, fetchMentionableEmployees, initializeEditor, destroyEditor } = useEditorInit({
  editor,
  editor_root,
  props,
  emit,
  closeLinkMenu,
});

const {
  highlight_picker_ref,
  text_color_picker_ref,
  show_highlight_menu,
  show_text_color_menu,
  toggleHighlightMenu,
  setHighlightColor,
  removeHighlight,
  closeHighlightMenu,
  toggleTextColorMenu,
  setTextColor,
  removeTextColor,
  closeTextColorMenu,
} = useEditorColors({
  editor,
  disabled: computed(() => props.disabled),
});

const { toolbarItems, setupKeyboardShortcuts, cleanupKeyboardShortcuts } = useEditorToolbar({
  editor,
  disabled: computed(() => props.disabled),
  setLink,
  toggleHighlightMenu,
  toggleTextColorMenu,
});

const editorContentProps = computed(() => ({
  class: {
    'editor-ziggu menu-bottom p-2 content rounded-xl!': true,
    [STATUS_COLORS[STATUS.ERROR].CARD_BORDER]: props.is_error,
    'bg-gray-100': props.disabled,
  },
  ...(props.disabled ? { disabled: true } : {}),
  editor: editor.value,
  style: [{ 'min-height': '40px !important' }],
}));

const editorBackgroundColor = computed(() => {
  if (!props.is_ticket) return 'background-color: transparent;';
  const backgrounds = { note_editor: '#fef6d7', default: '#f4f4f4' };
  const color = backgrounds[props.type] || backgrounds.default;
  return `background-color: ${color}; border: none !important;`;
});

onMounted(() => {
  initializeEditor();
  setupKeyboardShortcuts();
});

onBeforeUnmount(() => {
  closeLinkMenu({ focusEditor: false });
  destroyEditor();
  cleanupKeyboardShortcuts();
});

defineExpose({ editor, clearContent, fetchMentionableEmployees });
</script>

<style>
.disabled {
  pointer-events: none;
  background-color: #f3f4f6 !important;
  border-color: #f3f4f6 !important;
  color: #69748c !important;
}
.disabled button {
  background-color: #f3f4f6 !important;
  color: #69748c !important;
  pointer-events: none;
}

.disabled div {
  pointer-events: auto;
  cursor: not-allowed !important;
}

.ProseMirror-focused {
  outline: none;
}

.editor-ziggu p {
  margin-bottom: 0 !important;
}

.ProseMirror {
  min-height: 100% !important;
}

.editor-ziggu.menu-bottom {
  margin-bottom: 0rem !important;
}

.editor-ziggu {
  background-color: var(--editor-color) !important;
  overflow-y: auto;
  font-family: 'Inter';
  flex: 1 1 auto;
  min-height: 0;
}

.flex.shrink-0 {
  flex-shrink: 0 !important;
}

.editor-link-menu .input {
  width: 100%;
}

.ProseMirror p.is-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #a7a7a7;
  pointer-events: none;
  height: 0;
}

span[data-mention],
span[data-type='mention'] {
  background-color: #fbc916;
  border-radius: 0.3rem;
  padding: 0.1rem 0.3rem;
}

@media (max-width: 769px) {
  .editor-ziggu {
    min-height: 75px !important;
  }
}

.tippy-content .dropdown-menu {
  bottom: 100% !important;
  top: auto !important;
}

@media (max-width: 768px) {
  .editor-toolbar .toolbar-button {
    min-width: 44px;
    min-height: 44px;
  }

  .mobile-toolbar-container .toolbar-button {
    min-width: 44px;
    min-height: 44px;
  }
}

.mobile-toolbar-container {
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  transform: translateY(10px);
  transition:
    max-height 0.3s ease-in-out,
    opacity 0.3s ease-in-out,
    transform 0.3s ease-in-out;
}

.mobile-toolbar-visible {
  max-height: 200px;
  opacity: 1;
  transform: translateY(0);
}

.cursor-row-resize {
  touch-action: none;
}
</style>
